import { JobTable } from '@/components/table/JobTable';
import { TablePlaceholder } from '@/components/TablePlaceholder';
import { SelectChangeEvent } from '@mui/material';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { ConfirmationDialog, ManageJobsEvent } from 'klayowebshared';
import { debounce } from 'lodash';
import React, { SyntheticEvent } from 'react';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { track } from '../../common/track';
import { AttributeActionMenu } from '../../components/AttributeActionMenu';
import { Button } from '../../components/Button';
import { Paper } from '../../components/Paper';
import { TextField } from '../../components/TextField';
import { ViewComponent } from '../../components/ViewComponent';
import { JobBenchmarkingProvider } from '../../contexts/JobBenchmarkingContext';
import { StandardsLibraryProvider } from '../../contexts/StandardsLibraryContext';
import { SettingsEmployee } from '../../data/settings/SettingsEmployee';
import { SettingsJob } from '../../data/settings/SettingsJob';
import { SettingsJobList } from '../../data/settings/SettingsJobList';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import AddIcon from '../../resources/images/icons-add.svg';
import AiSparkleIcon from '../../resources/images/icons-ai-sparkle.svg';
import JobsIcon from '../../resources/images/icons-briefcase.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import { KLAYO_COLORS } from '../../themes';
import type {
  JobApiResponse,
  JobPosition,
  JobTableRef,
  MenuRef,
  SettingsJobsViewProps,
  SettingsJobsViewState,
} from '../../types/views/settingsJobs.types';
import { getMaxPossiblePageSize, trimToLowercase } from '../../utilities';
import { AddEditJobView } from './AddEditJobView';
import { AssignEmployeeDialog } from './AssignEmployeeDialog';
import { AssignManagerDialog } from './AssignManagerDialog';
import { ImportJobDescriptionDialog } from './ImportJobDescriptionDialog';
import { ImportJobFromIndustryStandardDialog } from './ImportJobFromIndustryStandard';
import JobBenchmarking from './JobBenchmarking';
import { SettingsJobBuilderView } from './SettingsJobBuilderView';
import { ShowSimilarJobDialog } from './ShowSimilarJobDialog';
import { canUseJobBuilder } from '../job-builder/jobBuilderAccess';

class SettingsJobsViewBase extends ViewComponent<
  SettingsJobsViewProps & WithLocaleProps,
  SettingsJobsViewState
> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  debounceTimer: ReturnType<typeof setTimeout> | null;
  actionTarget: HTMLElement | null;
  actionJob: SettingsJob | null;
  actionPosition: JobPosition | null;
  actionVacancy: JobPosition | null;
  menu: MenuRef | null;
  tableRef: JobTableRef | null;
  setTableRef: (tableRef: JobTableRef | null) => void;
  setMenuRef: (menu: MenuRef | null) => void;
  targetPosition: JobPosition | null;

  constructor(props: SettingsJobsViewProps & WithLocaleProps) {
    super(props);

    this.state = {
      search: null,
      showAssignEmployeeDialog: false,
      showAssignManagerDialog: false,
      showSimilarJobDialog: false,
      focusPosition: null,
      assignEmployeeError: null,
      assignManagerError: null,
      unassignEmployee: null,
      unassignEmployeeError: null,
      deletingJob: null,
      similarJob: null,
      deletingJobError: null,
      saveJobError: null,
      pageSize: SettingsJobList.defaultPageSize,
      deletedIds: [],
      newJobDialog: null,
      showImportJobFromIndustryStandardDialog: false,
      expandRowIds: [],
      showImportJobDescriptionDialog: false,
      showJobBenchmarkingDialog: false,
      jobBenchmarking: null,
      showUpgradeDialog: false,
    };

    this.debounceTimer = null;
    this.actionTarget = null;
    this.actionJob = null;
    this.actionPosition = null;
    this.actionVacancy = null;
    this.menu = null;
    this.tableRef = null;
    this.targetPosition = null;

    this.setMenuRef = (menu: MenuRef | null) => {
      this.menu = menu;
    };

    this.setTableRef = (tableRef: JobTableRef | null) => {
      this.tableRef = tableRef;
    };
  }

  trackSearch = debounce((search: string) => {
    if (search) track(ManageJobsEvent.Searched, { searchtext: search });
  }, 500);

  componentDidMount() {
    track(ManageJobsEvent.Viewed);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    const {
      onLoadJobs,
      onLoadAttributes,
      attributes,
      categories,
      onLoadCategories,
      onLoadManagers,
    } = this.props;
    if (!attributes) onLoadAttributes?.();
    if (!categories) onLoadCategories?.();

    onLoadJobs?.();
  }

  onViewChange(location: { pathname: string }): void {
    if (this.pathEquals('/settings/jobs') || this.pathEquals('/settings')) this.resetErrors();
  }

  resetErrors(): void {
    this.setState({
      saveJobError: null,
      deletingJobError: null,
      unassignEmployeeError: null,
      assignManagerError: null,
      assignEmployeeError: null,
    });
  }

  onSearch(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void {
    const { onLoadJobs } = this.props;
    const { pageSize } = this.state;

    // clearTimeout(this.debounceTimer);

    // this.debounceTimer = setTimeout(() => {
    //     onLoadJobs(true, e.target.value, pageSize);
    // }, TableSearchDebounceTime);

    this.trackSearch(e.target.value);
    this.setState({ search: e.target.value });
  }

  // load data of paging page from server
  onPageChange(page: number, pageSize: number): void {
    const { onLoadJobsPaging } = this.props;
    const { search } = this.state;

    // Data grid page start at 0
    if (onLoadJobsPaging) onLoadJobsPaging(page + 1, this.state.pageSize, search);
  }

  onPageSizeChange(pageSize: number, paginationMode: string): void {
    const { onLoadJobsPageSize, jobs } = this.props;
    const { search } = this.state;

    this.setState({ pageSize });

    if (paginationMode === 'server' && jobs && onLoadJobsPageSize) {
      let currentPage = jobs.currentPage;
      const maxPossiblePageSize = getMaxPossiblePageSize(jobs.totalCount, pageSize);

      // prevent currentPage from overflow when increase page size
      if (currentPage > maxPossiblePageSize) {
        currentPage = maxPossiblePageSize;
      }

      onLoadJobsPageSize(currentPage, pageSize, search);
    }
  }

  onPositionAction(e: React.MouseEvent<HTMLElement>, position: JobPosition): void {
    if (this.menu) this.menu.open(e.currentTarget);
    this.actionTarget = e.currentTarget;
    this.actionPosition = position;
    this.actionVacancy = position.isVacancy ? position : null;
    this.targetPosition = position;
  }

  onJobAction(e: React.MouseEvent<HTMLElement>, job: SettingsJob): void {
    if (this.menu) this.menu.open(e.currentTarget);
    this.actionTarget = e.currentTarget;
    this.actionJob = job;
  }

  onJobSelect(e: SyntheticEvent, job: SettingsJob): void {
    this.props.history.push('/settings/jobs/edit/' + job.jobDefinitionId);
  }

  onCloseActionMenu() {
    this.actionTarget = null;
    this.actionJob = null;
    this.actionPosition = null;
    this.actionVacancy = null;
    if (this.menu) this.menu.close();
  }

  onEditJob() {
    const { actionJob } = this;
    this.onCloseActionMenu();

    if (actionJob) {
      this.props.history.push('/settings/jobs/edit/' + actionJob.jobDefinitionId);
    }
  }

  onDeleteJob() {
    const { actionJob } = this;
    this.setState({ deletingJob: actionJob });
    this.onCloseActionMenu();
  }

  onRemoveFromJob() {
    const { actionPosition } = this;
    this.setState({ unassignEmployee: actionPosition });
    this.onCloseActionMenu();
  }

  // Personal-jobs (free-tier) non-admins don't have access to these org features; prompt them to
  // upgrade instead of running the action.
  requiresUpgrade(): boolean {
    const { user } = this.props;
    return !!user && !user.isOrganizationAdmin && !!user.hasPersonalJobs;
  }

  onUpgrade(): void {
    this.setState({ showUpgradeDialog: false });
    this.props.history.push('/pricing-and-checkout');
  }

  onCancelUpgrade(): void {
    this.setState({ showUpgradeDialog: false });
  }

  onAddVacancy(e: SyntheticEvent): void {
    if (this.requiresUpgrade()) {
      this.onCloseActionMenu();
      this.setState({ showUpgradeDialog: true });
      return;
    }

    const { jobs, onLoadJobs, onDataChanged } = this.props;
    const { actionJob } = this;
    if (!actionJob || !jobs) return;

    this.context?.setLoading?.('addjob', true);

    axiosClient
      .post('/Job', actionJob.toApiDto())
      .then((response: JobApiResponse) => {
        const [vacancy, job] = jobs.addVacancy(actionJob.jobDefinitionId, response.data);
        this.setState({
          focusPosition: actionJob as unknown as JobPosition,
          successSnackbar: this.props.translate('settingsViews.jobs.vacancyCreated'),
        });
        if (onDataChanged) onDataChanged('jobs');
        actionJob.numberOfPositions = job.numberOfPositions;
        this.tableRef?.apiRef?.current?.updateRows([vacancy]);
        // if (onLoadJobs) onLoadJobs(true);
      })
      .finally(() => {
        this.context?.setLoading?.('addjob', false);
      });

    this.setState({ focusPosition: actionJob as unknown as JobPosition });
    this.onCloseActionMenu();
  }

  onDeleteVacancy(e: SyntheticEvent): void {
    const { jobs, onLoadJobs, onDataChanged } = this.props;
    const { actionPosition } = this;

    if (!actionPosition || !jobs) return;

    const targetRowId = actionPosition?.rowId;
    const targetJobId = actionPosition?.job?.id;
    const jobRef = jobs?.jobs?.find((j: SettingsJob) => j.id === targetJobId);

    const originalJobPositions = jobRef?.numberOfPositions;
    const removedIndex = jobs?.positions?.findIndex((p: JobPosition) => p?.rowId === targetRowId);
    const removedItem =
      removedIndex !== undefined && removedIndex > -1 ? jobs?.positions?.[removedIndex] : null;

    if (jobRef) {
      jobRef.numberOfPositions = Math.max(0, (jobRef?.numberOfPositions || 0) - 1);
    }
    if (removedIndex !== undefined && removedIndex > -1) {
      jobs?.positions?.splice(removedIndex, 1);
      jobs?.updatePositionCount();
    }

    actionPosition.deleted = true;
    this.tableRef?.apiRef?.current?.updateRows([actionPosition]);

    this.context?.setLoading?.('deleting', true);

    axiosClient
      .delete('/Job', {
        data: {
          jobId: actionPosition.jobId,
        },
        withCredentials: true,
      })
      .then(() => {
        this.setState({
          successSnackbar: this.props.translate('settingsViews.jobs.vacancyDeleted'),
        });
        if (onDataChanged) onDataChanged('jobs');
      })
      .catch(() => {
        if (jobRef && typeof originalJobPositions === 'number') {
          jobRef.numberOfPositions = originalJobPositions;
        }
        if (removedIndex !== undefined && removedIndex > -1 && removedItem) {
          jobs?.positions?.splice(removedIndex, 0, removedItem);
          jobs?.updatePositionCount();
          actionPosition.deleted = false;
          this.tableRef?.apiRef?.current?.updateRows([actionPosition]);
        }
      })
      .finally(() => {
        this.context?.setLoading?.('deleting', false);
      });

    this.onCloseActionMenu();
  }

  onEditReportsTo() {
    const { actionPosition } = this;
    this.onCloseActionMenu();
    this.setState({ showAssignManagerDialog: true, targetPosition: actionPosition });
  }

  onAssignEmployee() {
    const { actionPosition } = this;
    this.onCloseActionMenu();
    this.setState({ showAssignEmployeeDialog: true, targetPosition: actionPosition });
  }

  onSaveAssignedEmployee(
    e: SyntheticEvent,
    position: JobPosition,
    employee: SettingsEmployee
  ): void {
    const { jobs, onDataChanged } = this.props;

    if (!jobs) return;

    this.context?.setLoading?.('assignEmployee', true);

    axiosClient
      .put(
        '/Job',
        {
          jobId: position?.jobId,
          employeeId: employee.employeeId,
          reportsToId: position?.manager ? position.manager?.jobId : null,
        },
        {
          withCredentials: true,
        }
      )
      .then(() => {
        const [updated, oldPosition] = jobs.replacePosition(position, employee);
        if (onDataChanged) onDataChanged('jobs');
        const updatedArray: JobPosition[] = [];
        if (updated && this.checkRowExistInNode(updated.job?.rowId)) updatedArray.push(updated);
        if (oldPosition) updatedArray.push(oldPosition);
        this.tableRef?.apiRef?.current?.updateRows(updatedArray);
        this.setState({
          successSnackbar: this.props.translate('settingsViews.jobs.employeeAssigned'),
          showAssignEmployeeDialog: false,
          targetPosition: null,
          assignEmployeeError: null,
        });
      })
      .finally(() => {
        this.context?.setLoading?.('assignEmployee', false);
      });
  }

  checkRowExistInNode(rowId: string | undefined): string | undefined {
    const allIds = this.tableRef?.apiRef?.current?.getAllRowIds() || [];
    const findId = allIds.find((id: string) => id?.includes(rowId || ''));
    return findId;
  }

  onSaveAssignedManager(
    e: SyntheticEvent,
    position: JobPosition,
    manager: JobPosition | null
  ): void {
    const { jobs, onLoadJobs, onDataChanged } = this.props;

    let jobId: string | null = null;
    if (position && position.jobId) {
      jobId = position.jobId;
    }

    if (!jobId) {
      this.setState({
        assignManagerError: this.props.translate('settingsViews.jobs.couldNotFindJobId'),
      });
      return;
    }

    this.context?.setLoading?.('assignManager', true);

    axiosClient
      .put('/Job', {
        jobId: jobId,
        employeeId: position?.employeeId || null,
        reportsToId: manager ? manager.jobId : null,
      })
      .then(() => {
        if (position && jobs) {
          position.manager = manager;
          jobs.updatePosition(position);
          this.tableRef?.apiRef?.current?.updateRows([position]);
        }

        if (onDataChanged) onDataChanged('jobs');

        // if (onLoadJobs) onLoadJobs(true);
        this.setState({
          assignManagerError: null,
          showAssignManagerDialog: false,
          targetPosition: null,
        });
      })
      .finally(() => {
        this.context?.setLoading?.('assignManager', false);
      });
  }

  onCancelAssignManager() {
    this.setState({
      showAssignManagerDialog: false,
      targetPosition: null,
      assignManagerError: null,
    });
  }

  onCancelAssignEmployee() {
    this.setState({
      showAssignEmployeeDialog: false,
      targetPosition: null,
      assignEmployeeError: null,
    });
  }

  onCancelUnassignEmployee(e: SyntheticEvent): void {
    this.setState({ unassignEmployee: null, unassignEmployeeError: null });
  }

  onUnassignEmployeeConfirmed(e: SyntheticEvent): void {
    const { jobs, onLoadJobs, onDataChanged } = this.props;
    const { unassignEmployee } = this.state;

    if (!unassignEmployee || !jobs) return;

    this.context?.setLoading?.('removeEmployee', true);

    axiosClient
      .put('/Job', {
        jobId: unassignEmployee.jobId,
        employeeId: null,
        reportsToId: unassignEmployee.manager ? unassignEmployee.manager.jobId : null,
      })
      .then(() => {
        jobs.vacatePosition(unassignEmployee);
        if (onDataChanged) onDataChanged('jobs');
        // if (onLoadJobs) onLoadJobs(true);
        this.tableRef?.apiRef?.current?.updateRows([unassignEmployee]);
        this.setState({ unassignEmployee: null, unassignEmployeeError: null });
      })
      .finally(() => {
        this.context?.setLoading?.('removeEmployee', false);
      });
  }

  onCancelDeleteJob(e: SyntheticEvent): void {
    this.setState({ deletingJob: null, deletingJobError: null });
  }

  onDeleteJobConfirmed(e: SyntheticEvent): void {
    const { jobs, onLoadJobs, onDataChanged } = this.props;
    const { deletingJob } = this.state;

    if (!deletingJob || !jobs) return;

    this.context?.setLoading?.('deleteJob', true);

    axiosClient
      .delete('/JobDefinition', {
        data: {
          jobDefinitionId: deletingJob.jobDefinitionId,
        },
      })
      .then(() => {
        this.setState({
          deletingJob: null,
          deletingJobError: null,
          successSnackbar: this.props.translate('settingsViews.jobs.jobDeleted'),
        });
        jobs.delete(deletingJob);
        if (onDataChanged) onDataChanged('jobs');
        if (onLoadJobs) onLoadJobs(true);
      })
      .finally(() => {
        this.context?.setLoading?.('deleteJob', false);
      });
  }

  onSaveJob(job: SettingsJob): void {
    const { jobs, onLoadJobs, onLoadAttributes, onDataChanged } = this.props;
    this.context?.setLoading?.('addJob', true);

    const newJob = new SettingsJob(job as any);

    if (!job.jobDefinitionId) {
      axiosClient
        .post('/JobDefinition', newJob.toApiDto(), {
          withCredentials: true,
        })
        .then(() => {
          if (onDataChanged) onDataChanged('jobs');
          if (onLoadJobs) onLoadJobs(true);
          if (onLoadAttributes) onLoadAttributes(true);
          this.props.history.push('/settings/jobs');
        })
        .finally(() => {
          this.context?.setLoading?.('addJob', false);
        });
    } else {
      axiosClient
        .put('/JobDefinition', newJob.toApiDto())
        .then(() => {
          //jobs.update(job);
          if (onDataChanged) onDataChanged('jobs');
          if (onLoadJobs) onLoadJobs(true);
          if (onLoadAttributes) onLoadAttributes(true);
          this.props.history.push('/settings/jobs');
        })
        .finally(() => {
          this.context?.setLoading?.('addJob', false);
        });
    }
  }
  onOpenSimilarJobDialog() {
    if (this.requiresUpgrade()) {
      this.onCloseActionMenu();
      this.setState({ showUpgradeDialog: true });
      return;
    }

    const { actionJob } = this;

    this.setState({ showSimilarJobDialog: true, similarJob: actionJob });
    this.onCloseActionMenu();
  }

  onCloseSimilarJobDialog() {
    this.setState({ showSimilarJobDialog: false, similarJob: null });
  }

  onCloseImportFromStandardLib() {
    this.setState({ showImportJobFromIndustryStandardDialog: false });
  }

  onCloseImportJobDescriptionDialog() {
    this.setState({ showImportJobDescriptionDialog: false });
  }

  onCloseImportJobBenchmarkingDialog() {
    this.setState({ showJobBenchmarkingDialog: false, jobBenchmarking: null });
  }

  onOpenJobBenchmarkingDialog() {
    if (this.requiresUpgrade()) {
      this.onCloseActionMenu();
      this.setState({ showUpgradeDialog: true });
      return;
    }

    const { actionJob } = this;

    this.setState({ showJobBenchmarkingDialog: true, jobBenchmarking: actionJob });
  }

  onOpenImportFromStandardLib() {
    this.handleClose(null);
    this.setState({ showImportJobFromIndustryStandardDialog: true });
  }
  onOpenImportJobDescriptionDialog() {
    this.handleClose(null);
    this.setState({ showImportJobDescriptionDialog: true });
  }

  renderActionMenu() {
    const { actionVacancy, actionJob, actionPosition } = this;
    const { translate, organization } = this.props;
    if (!!actionVacancy || !!actionPosition) {
      return (
        <div>
          {actionVacancy && (
            <div>
              <MenuItem
                onClick={(e: any) => {
                  this.trackMenuOption('assign_employee');
                  this.onAssignEmployee();
                }}
              >
                <ListItemText>{translate('settingsViews.jobs.assignEmployee')}</ListItemText>
              </MenuItem>
              <Divider />
            </div>
          )}
          <MenuItem
            onClick={(e: any) => {
              this.trackMenuOption('edit_reports_to');
              this.onEditReportsTo();
            }}
          >
            <ListItemText>{translate('settingsViews.jobs.editReportsTo')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e: any) => {
              this.trackMenuOption(actionVacancy ? 'delete_vacancy' : 'remove_employee');
              actionVacancy ? this.onDeleteVacancy(e) : this.onRemoveFromJob();
            }}
          >
            <ListItemText>
              {actionVacancy
                ? translate('settingsViews.jobs.deleteVacancy')
                : translate('settingsViews.jobs.removeEmployee')}
            </ListItemText>
          </MenuItem>
        </div>
      );
    }
    if (actionJob) {
      return (
        <div>
          <MenuItem
            onClick={() => {
              this.trackMenuOption('edit_job');
              this.onEditJob();
            }}
          >
            <ListItemText>{translate('settingsViews.jobs.editJob')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              this.trackMenuOption('delete_job');
              this.onDeleteJob();
            }}
            disabled={actionJob ? actionJob.numberOfPositions !== 0 : true}
          >
            <ListItemText>{translate('settingsViews.jobs.deleteJob')}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={(e: any) => {
              this.trackMenuOption('add_vacancy');
              this.onAddVacancy(e);
            }}
          >
            <ListItemText>{translate('settingsViews.jobs.addVacancy')}</ListItemText>
          </MenuItem>
          {this.props.jobs?.totalCount > 1 && (
            <MenuItem
              onClick={() => {
                this.trackMenuOption('view_similar_jobs');
                this.onOpenSimilarJobDialog();
              }}
            >
              <ListItemText>{translate('settingsViews.jobs.viewSimilarJobs')}</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              this.trackMenuOption('benchmark');
              this.onOpenJobBenchmarkingDialog();
            }}
          >
            <ListItemText>{translate('settingsViews.jobs.benchmark')}</ListItemText>
          </MenuItem>
        </div>
      );
    }
  }

  onGoBackToJobs() {
    const { history, onLoadJobs, location } = this.props;

    // The add-job form was opened from the Job Builder "Start from scratch" flow (which set
    // location.state.from). Go back rather than push, so we land on the existing Job Builder
    // history entry instead of stacking a duplicate — otherwise Job Builder's own Cancel
    // (navigate(-1)) would return to this form instead of the page before it. See ticket #3208.
    const from = (location.state as { from?: string } | null)?.from;
    if (from && from.startsWith('/') && !from.startsWith('//')) {
      history.goBack();
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const navigateMode = queryParams.get('navigateMode');

    if (navigateMode === 'task') {
      history.push('/settings/tasks');
    }
    if (navigateMode === 'competencies') {
      history.push('/settings/competencies');
    } else {
      history.push('/settings/jobs');
      if (onLoadJobs) onLoadJobs(true);
    }
  }

  handleClose(e: SyntheticEvent | null): void {
    this.setState({ newJobDialog: null });
  }

  goToNewJob(): void {
    const { history } = this.props;
    track(ManageJobsEvent.NewJob);
    this.setState({ newJobDialog: null });
    history.push('/settings/jobs/builder');
  }

  goToBenchmarkJob(): void {
    track(ManageJobsEvent.Benchmark);
    this.props.history.push('/job-benchmarking');
  }

  onExpandRowChange(expandRowIds: string[]): void {
    if (expandRowIds.length > (this.state.expandRowIds?.length ?? 0)) {
      track(ManageJobsEvent.ExpandJob);
    }
    this.setState({ expandRowIds });
  }

  trackMenuOption(optionInfo: string) {
    track(ManageJobsEvent.MenuOption, { optionInfo });
  }

  render() {
    const {
      organization,
      theme,
      user,
      location,
      history,
      jobs,
      employees,
      managers,
      onDataChanged,
      onLoadEmployees,
      onLoadManagers,
      onShowNewAttributeDialog,
      onNavigation,
      onBlockNavigation,
      onAllowNavigation,
      translate,
    } = this.props;
    const {
      search,
      focusPosition,
      assignEmployeeError,
      assignManagerError,
      unassignEmployee,
      unassignEmployeeError,
      deletingJob,
      deletingJobError,
      saveJobError,
      showSimilarJobDialog,
      newJobDialog,
      showImportJobFromIndustryStandardDialog,
      showAssignManagerDialog,
      showAssignEmployeeDialog,
      targetPosition,
      showImportJobDescriptionDialog,
      showJobBenchmarkingDialog,
      jobBenchmarking,
    } = this.state;

    const path = location.pathname;
    const editMode = path.startsWith('/settings/jobs/edit/');
    // The Job Builder API rejects non-admin instructors (see canUseJobBuilder); hide its entry.
    const canBuildJobs = canUseJobBuilder(user);
    const showJobBuilder = canBuildJobs && path.startsWith('/settings/jobs/builder');
    const showAddEditJobView = path.startsWith('/settings/jobs/new') || editMode;

    let editJob = null;
    let editJobId = null;
    if (editMode) editJobId = path.replace('/settings/jobs/edit/', '');
    if (jobs) editJob = jobs.get(editJobId);

    const queryParams = new URLSearchParams(location.search);
    const navigateMode = queryParams.get('navigateMode');

    let assignToPosition = null;
    let assignToPositionId = null;
    if (showAssignEmployeeDialog)
      assignToPositionId = path.replace('/settings/jobs/assign-employee/', '');
    if (jobs) assignToPosition = jobs.getPosition(assignToPositionId);

    if (!assignToPosition && assignToPositionId && showAssignManagerDialog) {
      assignToPosition = { jobId: assignToPositionId };
    }

    const filterJobs = jobs
      ? [...jobs.jobs, ...jobs.positions].filter((pos) => {
          if (search) {
            const keyword = trimToLowercase(search || '');
            const jobName = trimToLowercase(pos?.name || '');
            const posName = trimToLowercase(pos?.job?.name || '');
            return pos?.isJob ? jobName?.includes(keyword) : posName?.includes(keyword);
          } else {
            return true;
          }
        })
      : [];

    return (
      <>
        <Menu
          open={newJobDialog !== null}
          anchorEl={newJobDialog}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={this.handleClose.bind(this) as any}
        >
          <MenuItem onClick={this.goToNewJob.bind(this)}>
            <ListItemText>{translate('settingsViews.jobs.startNewEmptyJob')}</ListItemText>
          </MenuItem>

          <MenuItem onClick={this.onOpenImportFromStandardLib.bind(this)}>
            <ListItemText>
              {translate('settingsViews.jobs.startFromKlayoIntelligence')}
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={this.onOpenImportJobDescriptionDialog.bind(this)}>
            <ListItemText>{translate('settingsViews.jobs.startFromJobDescription')}</ListItemText>
          </MenuItem>
        </Menu>
        {showJobBuilder && (
          <SettingsJobBuilderView theme={theme} onImported={() => this.props.onLoadJobs?.(true)} />
        )}

        {!showAddEditJobView && !showJobBuilder && (
          <Paper
            theme={theme}
            padding={{ xs: '46px 24px', md: '60px!important' }}
            borderFromBreakpoint='md'
          >
            <h1>{translate('settingsViews.jobs.title')}</h1>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', padding: '30px 0' }}
            >
              <TextField
                value={search}
                dense={true}
                placeholder={translate('settingsViews.jobs.searchJobs')}
                disabled={user === null}
                fullWidth={true}
                autoComplete={false}
                leadingIcon={<SearchIcon />}
                onChange={this.onSearch.bind(this)}
                sx={{ maxWidth: { md: '300px' } }}
              />

              <Stack direction='row' alignItems='center' spacing={1.5}>
                <Button
                  size='md'
                  theme={theme}
                  variant='outlined'
                  label={translate('settingsViews.jobs.benchmarkJob')}
                  onClick={this.goToBenchmarkJob.bind(this)}
                  startIcon={<AiSparkleIcon color={KLAYO_COLORS.NeonBlueNB500} />}
                  sx={{
                    flex: {
                      xs: 1,
                      sm: 'auto',
                    },
                  }}
                />
                {canBuildJobs && (
                  <Button
                    size='md'
                    theme={theme}
                    variant='filled'
                    label={translate('settingsViews.jobs.newJob')}
                    onClick={this.goToNewJob.bind(this)}
                    startIcon={<AddIcon />}
                    sx={{
                      flex: {
                        xs: 1,
                        sm: 'auto',
                      },
                    }}
                  />
                )}
              </Stack>
            </Stack>

            <JobTable
              {...({
                ref: this.setTableRef,
                deletedIds: this.state.deletedIds,
                minHeight: '300px',
                theme: theme,
                showHeaderFromBreakpoint: 'md',
                rowHasAction: true,
                filterMode: 'client',
                dense: true,
                tree: true,
                paginationMode: 'client',
                totalCount: this.props.jobs?.totalCount,
                pageSize: 50,
                onPageSizeChange: this.onPageSizeChange.bind(this),
                onJobSelect: this.onJobSelect.bind(this),
                onJobAction: this.onJobAction.bind(this),
                onPositionAction: this.onPositionAction.bind(this),
                focusPosition: focusPosition,
                onExpandRowChange: this.onExpandRowChange.bind(this),
                expandedRowIds: this.state.expandRowIds,
                paper: false,
                jobs: jobs ? jobs : null,
                rows: filterJobs,
              } as any)}
              columns={[
                {
                  type: JobTable.columns.profilePicFullName,
                  label: translate('tableColumns.name'),
                },
                { type: JobTable.columns.reportsTo, label: translate('tableColumns.reportsTo') },
                { type: JobTable.columns.reports, label: translate('tableColumns.reports') },
                { type: JobTable.columns.positions, label: translate('tableColumns.positions') },
                { type: JobTable.columns.actions, label: translate('tableColumns.actions') },
              ]}
              noRowsComponent={
                <TablePlaceholder
                  text={translate('settingsViews.jobs.noJobsPlaceholder')}
                  icon={<JobsIcon />}
                />
              }
              keyword={search ?? undefined}
            />
          </Paper>
        )}

        <AttributeActionMenu
          ref={this.setMenuRef}
          onClose={this.onCloseActionMenu.bind(this)}
          onRender={this.renderActionMenu.bind(this)}
        />

        {showAddEditJobView && (
          <AddEditJobView
            {...(this.props as any)}
            editMode={editMode}
            theme={theme}
            error={saveJobError}
            onShowNewAttributeDialog={onShowNewAttributeDialog}
            job={editMode ? editJob : new SettingsJob()}
            jobId={editJobId ?? undefined}
            onBlockNavigation={onBlockNavigation}
            onAllowNavigation={onAllowNavigation}
            onDataChanged={onDataChanged}
            onNavigation={onNavigation}
            onCancel={this.onGoBackToJobs.bind(this)}
            onSave={this.onSaveJob.bind(this)}
            navigateMode={navigateMode ?? undefined}
            organization={organization}
          />
        )}
        {showSimilarJobDialog && (
          <ShowSimilarJobDialog
            theme={theme}
            onClose={this.onCloseSimilarJobDialog.bind(this)}
            row={{
              selectedJob: this.state.similarJob ?? undefined,
            }}
          />
        )}

        <Fade in={showImportJobFromIndustryStandardDialog} unmountOnExit>
          <div>
            <ImportJobFromIndustryStandardDialog
              theme={theme}
              open={showImportJobFromIndustryStandardDialog}
              onClose={this.onCloseImportFromStandardLib.bind(this)}
              organization={organization}
            />
          </div>
        </Fade>

        <Fade in={showImportJobDescriptionDialog} unmountOnExit>
          <div>
            <StandardsLibraryProvider>
              <ImportJobDescriptionDialog
                theme={theme}
                open={showImportJobDescriptionDialog}
                onClose={this.onCloseImportJobDescriptionDialog.bind(this)}
                organization={organization}
              />
            </StandardsLibraryProvider>
          </div>
        </Fade>

        <Fade in={showJobBenchmarkingDialog} unmountOnExit>
          <div>
            <JobBenchmarkingProvider>
              <JobBenchmarking
                open={showJobBenchmarkingDialog}
                onClose={this.onCloseImportJobBenchmarkingDialog.bind(this)}
                selectedJob={jobBenchmarking ?? undefined}
                theme={theme}
              />
            </JobBenchmarkingProvider>
          </div>
        </Fade>

        {showAssignEmployeeDialog && (
          <AssignEmployeeDialog
            {...({
              employees,
              theme,
              position: targetPosition ?? undefined,
              onLoadEmployees,
              onDataChanged,
              error: assignEmployeeError ?? undefined,
              onSave: this.onSaveAssignedEmployee.bind(this),
              onCancel: this.onCancelAssignEmployee.bind(this),
            } as any)}
          />
        )}

        {showAssignManagerDialog && (
          <AssignManagerDialog
            {...({
              managers,
              theme,
              position: targetPosition ?? undefined,
              manager: targetPosition ? targetPosition.manager : null,
              onLoadManagers,
              onDataChanged,
              error: assignManagerError ?? undefined,
              allowNone: true,
              onSave: this.onSaveAssignedManager.bind(this),
              onCancel: this.onCancelAssignManager.bind(this),
            } as any)}
          />
        )}

        {unassignEmployee && (
          <ConfirmationDialog
            theme={theme}
            title={translate('settingsViews.jobs.removeEmployeeTitle')}
            question={
              <div>
                {translate('settingsViews.jobs.removeEmployeeConfirm', {
                  employeeName: unassignEmployee.fullName,
                  jobName: unassignEmployee.job?.name,
                })}
              </div>
            }
            acceptButton={translate('settingsViews.jobs.removeButton')}
            error={unassignEmployeeError}
            onCancel={this.onCancelUnassignEmployee.bind(this)}
            onAccept={this.onUnassignEmployeeConfirmed.bind(this)}
          />
        )}

        {deletingJob && (
          <ConfirmationDialog
            theme={theme}
            title={translate('settingsViews.jobs.deleteJobTitle')}
            question={<div>{translate('settingsViews.jobs.deleteJobConfirm')}</div>}
            acceptButton={translate('settingsViews.jobs.deleteJobButton')}
            acceptDanger={true}
            error={deletingJobError}
            onCancel={this.onCancelDeleteJob.bind(this)}
            onAccept={this.onDeleteJobConfirmed.bind(this)}
          />
        )}

        {this.state.showUpgradeDialog && (
          <ConfirmationDialog
            theme={theme}
            title={translate('settingsViews.jobs.upgradeTitle')}
            question={<div>{translate('settingsViews.jobs.upgradeMessage')}</div>}
            acceptButton={translate('common.upgrade')}
            cancelButton={translate('common.cancel')}
            onCancel={this.onCancelUpgrade.bind(this)}
            onAccept={this.onUpgrade.bind(this)}
          />
        )}

        {this.renderSnackbar()}
      </>
    );
  }
}

export const SettingsJobsView = withLocale(SettingsJobsViewBase);
